/**
 * The shared confirm/reveal panel container — **one implementation of `role="dialog"` for the
 * whole package.**
 *
 * ## ⛔ WHY `aria-modal="true"` IS GONE, AND IS NOT COMING BACK
 *
 * Nine panels declared `role="dialog" aria-modal="true"` while being **inline, in-flow panels**:
 * `rounded-lg border … p-3/p-4`, no `fixed`, no overlay, no backdrop, no `inert` and no
 * `aria-hidden` on anything else. `aria-modal="true"` instructs assistive technology to **prune
 * everything outside the dialog** from the accessibility tree — so a screen-reader user who opened
 * a Suspend confirmation lost the tenant name, the status badge, the placement card and every other
 * error state on the page, while a keyboard user could Tab straight out of the panel into the
 * still-live page behind it because nothing was made inert. ⛔ The attribute was false in both
 * directions at once, and a false modality claim is worse for an AT user than omitting it.
 *
 * These panels are not modals and should not become modals: a true modal needs an overlay, a focus
 * trap and `inert` on the rest of the document. So the honest shape is a **labelled, non-modal
 * dialog** — `role="dialog"` + `aria-labelledby` — with the two things that were actually missing:
 *
 *  1. **Focus moves into the panel on mount.** `role="dialog"` is not a live region, so without this
 *     a screen-reader user was never told the panel appeared at all: focus stayed on the trigger and
 *     the confirm/cancel buttons sat somewhere further down the reading order. Moving focus to the
 *     container is what makes AT announce the dialog and its accessible name. ⚠️ The CONTAINER, not
 *     the first control — focusing "Confirm" would put initial focus on the destructive choice, and
 *     focusing "Cancel" would announce a button before the sentence explaining what is being
 *     confirmed.
 *  2. **Focus returns to the opener on close.** Adding focus-in without focus-out would trade one
 *     defect for another: the panel unmounts under the user's focus and dumps them at the top of the
 *     document. The opener is captured at mount from `document.activeElement`, which IS the trigger
 *     the operator just activated. One opener cannot be read that way: a menuitem of `ActionMenu`
 *     unmounts with its menu in the commit that mounts the dialog, so focus sits on nothing. The
 *     menu offers its trigger through {@link offerDialogOpener} for that one commit, and the panel
 *     takes it as the opener (R-063 part 5).
 *
 * ## ⚠️ `onEscape` is NULLABLE, and that is not laziness
 *
 * Escape must do exactly what Cancel does — for a confirmation, dismiss it. But
 * {@link DataPlanePane}'s setup-token reveal has **no cancel**: its Close is deliberately `disabled`
 * until "I have copied this token" is ticked, because the control plane persists no token material
 * and closing without copying means rotating again. ⛔ Wiring Escape there would hand back a
 * keystroke that discards a secret the server will never resend — i.e. it would route around the
 * one gate that exists. So that panel passes `null` and Escape is inert on it, by decision.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Focus the panel container.
 *
 * ⛔ Exported, and it takes the element as a PARAMETER, so both arms are reachable from a direct
 * unit test. Inlining `ref.current?.focus()` at the call site would create a null arm that no test
 * can enter — the ref is always populated in a committed effect — and a permanently-uncovered
 * branch is exactly what this package refuses to buy with a suppression.
 */
export function focusPanel(el: HTMLElement | null): void {
  if (el !== null) el.focus();
}

/**
 * Return focus to whatever held it when the panel opened.
 *
 * ⛔ `document.activeElement` is typed `Element | null` and only an `HTMLElement` is focusable, so
 * the narrowing is real rather than defensive — and taking the value as a parameter is what makes
 * the `null`/non-HTML arm testable without staging an exotic document state.
 */
export function restoreFocus(opener: Element | null): void {
  if (opener instanceof HTMLElement) opener.focus();
}

/**
 * The element a caller has offered as the next dialog's opener, or `null`. One slot, because one
 * element holds focus at a time. It lives for one commit: the `DialogPanel` or `ConfirmDialog` that
 * mounts in that commit takes it. `ActionMenu` also withdraws its own offer after the commit when no
 * dialog took it. The other producers do not withdraw: each one offers in the same click handler
 * that sets the state which mounts its dialog, so the dialog takes the offer in that commit.
 */
let offeredOpener: HTMLElement | null = null;

/**
 * Offer `el` as the opener of a `DialogPanel` or a `ConfirmDialog` that mounts in the current commit.
 *
 * Callers:
 * - `ActionMenu`'s `closeForDialog`, with its trigger. A menuitem that opens a dialog unmounts in the
 *   same commit, so without the offer the panel would record no opener and return focus to the
 *   document body on close.
 * - `RegionsPanel.setupSaved()` in `dp-satellites-panel.tsx`, with the Set up button. It offers the
 *   button before it swaps the setup panel for the progress panel of the saved satellite. The pressed
 *   Save button unmounts in that commit.
 * - `InFlightPane`'s remedy buttons and `TenantSettingsTab`'s lifecycle verbs, with the pressed
 *   button, before they open a `ConfirmDialog`. A mouse press in Safari, and in Firefox on macOS,
 *   focuses no button, so the focused element is not the opener.
 * - `OnboardOwnerInvitation`, with its card heading, in the task that hands a reissued link to the
 *   pane, whose link dialog mounts in that commit. The read after the reissue removes the Reissue
 *   button, so the heading is the opener that stays. The card withdraws the offer after the commit.
 *
 * A caller must open its dialog in the same commit, or call {@link withdrawDialogOpener} after it,
 * so no later, unrelated dialog takes the offer. `null` clears the slot. Side effect: replaces any
 * earlier offer.
 */
export function offerDialogOpener(el: HTMLElement | null): void {
  offeredOpener = el;
}

/**
 * Withdraw `el`'s offer if it is still in the slot; an offer from another element stays.
 *
 * Caller: `ActionMenu`'s close effect, which runs after every layout effect of the commit, so a panel
 * that mounted in that commit has already taken the offer. An item that opened no dialog leaves an
 * offer nobody took, and this removes it before a later, unrelated panel could read it.
 */
export function withdrawDialogOpener(el: HTMLElement | null): void {
  if (offeredOpener === el) offeredOpener = null;
}

/**
 * The click handler of a row button that opens a dialog: it offers the pressed button through
 * {@link offerDialogOpener}, then calls `act`, which sets the state that mounts the dialog in the
 * same commit. Callers: the row buttons of the OAuth Clients and Self-registration tabs, and the Set
 * up button and the row actions of `RegionsPanel` in `dp-satellites-panel.tsx`.
 */
export function opening(event: React.MouseEvent<HTMLButtonElement>, act: () => void): void {
  offerDialogOpener(event.currentTarget);
  act();
}

/**
 * The opener a mounting dialog records: the offered element when one is in the slot, else `focused`
 * (the dialog passes `document.activeElement`). Empties the slot either way, so one offer serves one
 * dialog.
 *
 * Callers: `DialogPanel`, in a layout effect at mount, and `ConfirmDialog`, in the state initializer
 * of its open content during the first render. Both run before `ActionMenu`'s passive close effect,
 * so each takes an offer before the menu withdraws it.
 */
export function takeDialogOpener(focused: Element | null): Element | null {
  const offered = offeredOpener;
  offeredOpener = null;
  return offered ?? focused;
}

/** The keyCode that some browsers send for a key event inside an IME composition. */
const IME_KEY_CODE = 229;

/**
 * True when a key event belongs to an IME composition (`isComposing`, or the `keyCode` 229 that
 * some browsers send while one runs). An IME ends a composition with Enter or Escape, so a handler
 * reads no command from such a key. Callers: `DialogPanel`, `ActionMenu`'s search field and
 * `RightsDecision`. Pure.
 */
export function isImeKey(event: { readonly nativeEvent: { readonly isComposing: boolean }; readonly keyCode: number }): boolean {
  return event.nativeEvent.isComposing || event.keyCode === IME_KEY_CODE;
}

/** ⛔ Not a `KeyboardEvent` handler in general — only Escape, and only when a cancel path exists. */
export function handleDialogKey(key: string, onEscape: (() => void) | null): void {
  if (key === 'Escape' && onEscape !== null) onEscape();
}

export function DialogPanel({
  labelledBy,
  testId,
  className,
  onEscape,
  dataAction,
  dataWrite,
  children,
}: {
  /** The id of the heading inside — every call site already renders one. */
  readonly labelledBy: string;
  readonly testId: string;
  readonly className: string;
  /** ⛔ `null` ⇒ Escape is inert. See this file's docblock for the one panel that wants that. */
  readonly onEscape: (() => void) | null;
  /**
   * The marker attribute a panel's own assertions key on.
   *
   * ⚠️ TWO NAMES, deliberately. `DataPlanePane`'s lifecycle dialog carries `data-action` and
   * `PeoplePane`'s write dialog carries `data-write`, and both are pinned by tests in their own
   * files. Collapsing them to one name here would be a silent contract change to two suites for a
   * cosmetic gain, so this component preserves each. Both absent ⇒ React omits both attributes.
   */
  readonly dataAction?: string;
  readonly dataWrite?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const opener = useRef<Element | null>(null);
  /*
   * A layout effect, so the opener is taken before `ActionMenu`'s passive close effect withdraws an
   * offer: React runs every layout effect of a commit before any passive effect of it.
   */
  useLayoutEffect(() => {
    opener.current = takeDialogOpener(document.activeElement);
  }, []);
  useEffect(() => {
    const openedBy = opener.current;
    focusPanel(ref.current);
    return () => restoreFocus(openedBy);
  }, []);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-labelledby={labelledBy}
      data-testid={testId}
      data-action={dataAction}
      data-write={dataWrite}
      /*
       * ⛔ `-1`, so the container is programmatically focusable but never a Tab stop of its own —
       * Tab from here goes to the first control inside, which is the reading order the panel wants.
       */
      tabIndex={-1}
      /*
       * React bubbles a key event out of a portal into this panel. A portalled child that handled
       * Escape (a WARD Select closes its list) calls `preventDefault()`, and the panel stays open. An
       * Escape that ends an IME composition (`isImeKey`) keeps the panel open too.
       */
      onKeyDown={(event) => {
        if (!event.defaultPrevented && !isImeKey(event)) handleDialogKey(event.key, onEscape);
      }}
      className={className}
    >
      {children}
    </div>
  );
}
